import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Chip, { ChipProps } from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import colors from '@/assets/scss/_themes-vars.module.scss'
import { TranscriptionSegment, TranscriptionSpeaker } from '@/core/models/transcription'

import { SpeakerActivity, SpeakerNames, formatSegmentTime, groupBySpeaker } from '../model/transcript'

/** Theme colours only: staff in primary (doctor filled, nurse outlined), patient neutral grey. */
const SPEAKER_CHIP: Record<TranscriptionSpeaker, Pick<ChipProps, 'color' | 'variant'>> = {
  doctor: { color: 'primary', variant: 'filled' },
  pds: { color: 'primary', variant: 'outlined' },
  patient: { color: 'default', variant: 'filled' },
  unknown: { color: 'default', variant: 'outlined' },
}

export type TranscriptListProps = {
  segments: TranscriptionSegment[]
  interim: Partial<Record<TranscriptionSpeaker, string>>
  activity?: Partial<Record<TranscriptionSpeaker, SpeakerActivity>>
  emptyHint: string
  /** Real names of the people on the call; a speaker without one falls back to the role label. */
  names?: SpeakerNames
}

export function useSpeakerLabel() {
  const { t } = useTranslation()
  return (speaker: TranscriptionSpeaker) => t(`page.liveConsultation.transcription.speaker.${speaker}`)
}

function useSegmentTime() {
  const { i18n } = useTranslation()
  return (paragraph: Parameters<typeof formatSegmentTime>[0]) => formatSegmentTime(paragraph, i18n.language)
}

export function TranscriptList({ segments, interim, activity = {}, emptyHint, names = {} }: TranscriptListProps) {
  const { t } = useTranslation()
  const speakerLabel = useSpeakerLabel()
  const speakerChip = (speaker: TranscriptionSpeaker, extra?: Partial<ChipProps>) => {
    const name = names[speaker]
    const chip = <Chip size="small" {...SPEAKER_CHIP[speaker]} label={name ?? speakerLabel(speaker)} sx={{ minWidth: 88, maxWidth: 180, fontWeight: 500 }} {...extra} />
    return name ? <Tooltip title={speakerLabel(speaker)}>{chip}</Tooltip> : chip
  }
  const segmentTime = useSegmentTime()
  const paragraphs = useMemo(() => groupBySpeaker(segments), [segments])
  const bottomRef = useRef<HTMLDivElement>(null)
  const interimEntries = Object.entries(interim).filter(([, text]) => !!text) as Array<[TranscriptionSpeaker, string]>
  // A speaker with partial text already has a live row; only show the activity row otherwise.
  const activityEntries = (Object.entries(activity) as Array<[TranscriptionSpeaker, SpeakerActivity]>).filter(
    ([speaker, state]) => state !== 'idle' && !interim[speaker]
  )

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [paragraphs.length, interimEntries.length, activityEntries.length])

  if (!paragraphs.length && !interimEntries.length && !activityEntries.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: colors.gray500 }}>
          {emptyHint}
        </Typography>
      </Box>
    )
  }

  return (
    <Stack spacing={2} data-testid="transcript-list">
      {paragraphs.map((paragraph, index) => (
        <Stack key={`${paragraph.speaker}-${paragraph.start}-${index}`} direction="row" spacing={1.5} alignItems="flex-start">
          {speakerChip(paragraph.speaker)}
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: colors.gray500 }}>
              {segmentTime(paragraph)}
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {paragraph.texts.join(' ')}
            </Typography>
          </Box>
        </Stack>
      ))}
      {interimEntries.map(([speaker, text]) => (
        <Stack key={`interim-${speaker}`} direction="row" spacing={1.5} alignItems="flex-start" data-testid="transcript-interim">
          {speakerChip(speaker, { variant: 'outlined' })}
          <Typography variant="body1" sx={{ color: colors.gray500, fontStyle: 'italic' }}>
            {text}
          </Typography>
        </Stack>
      ))}
      {activityEntries.map(([speaker, state]) => (
        <Stack key={`activity-${speaker}`} direction="row" spacing={1.5} alignItems="center" data-testid="transcript-activity" data-activity={state}>
          {speakerChip(speaker, { variant: 'outlined' })}
          {state === 'processing' ? (
            <CircularProgress size={14} thickness={5} />
          ) : (
            <Box component="span" sx={{ display: 'inline-flex', gap: '3px', '& i': { width: 6, height: 6, borderRadius: '50%', backgroundColor: colors.gray500, animation: 'transcript-dot 1.2s infinite' }, '& i:nth-of-type(2)': { animationDelay: '0.2s' }, '& i:nth-of-type(3)': { animationDelay: '0.4s' }, '@keyframes transcript-dot': { '0%, 80%, 100%': { opacity: 0.25 }, '40%': { opacity: 1 } } }}>
              <i /><i /><i />
            </Box>
          )}
          <Typography variant="body2" sx={{ color: colors.gray500, fontStyle: 'italic' }}>
            {t(`page.liveConsultation.transcription.activity.${state}`)}
          </Typography>
        </Stack>
      ))}
      <div ref={bottomRef} />
    </Stack>
  )
}
